// sectorSourcePolicy.ts — WHICH SOURCE DECIDES A FUND'S SECTOR SPLIT.
//
// One definition, imported by both consumers, because there are two implementations of this
// arithmetic (src/lib/blendedPortfolioMatrix.ts for the dashboards and the adviser presentation,
// src/components/benchmark/BlendedSectorWeightings.tsx for the sector-weightings pages) and they
// are required to agree — `blendedPortfolioMatrix`'s own header says so. Two bare `0.5` literals
// in two files is exactly how they would quietly stop agreeing.
//
// THE POLICY: MANUAL FIRST, FE AS BACKUP.  (Sam, 2026-08-20)
//
// A fund's split comes from its manual `ba_cbam_fund_sector_mappings` rows whenever it has any.
// FE's own asset breakdown is used ONLY for a fund with no manual mapping at all. FE is the
// fallback, not a co-author.
//
// WHAT THIS REPLACED, AND WHY IT MATTERED. The previous rule averaged the two 50/50 whenever both
// were available:
//
//     const manualShare = hasManual && hasFe ? 0.5 : hasManual ? 1 : 0;
//     const feShare     = hasManual && hasFe ? 0.5 : hasFe     ? 1 : 0;
//
// That was live and it was the majority case — measured 2026-08-20 on the P suite, the blend
// fired for 22 of 27 held funds, so most published sector weightings were half FE's numbers. It
// had also drifted from how the desk understood its own figures, and nothing recorded the
// intent either way, which is the reason this file exists rather than a corrected literal.
//
// The 50/50 was not indefensible — the FE data behind it is mostly clean (of the 25 held funds
// with an FE row, 16 totalled exactly 100.00 and 6 more within 0.1). It was simply not what the
// numbers were understood to mean, and a published figure that nobody can describe is worse than
// a slightly less sophisticated one that everybody can.
//
// CONSEQUENCE WORTH KNOWING: every one of the 27 currently-held P funds HAS a manual mapping, so
// under this policy the FE leg does not fire at all today. FE becomes a genuine backup — it waits
// for a fund that has no manual mapping yet — and, separately, a cross-check worth running against
// the manual numbers. It is NOT dead: `resolveFundCbam` still runs, and `sectorSourceFor` still
// reports what FE would have contributed, which is what makes the disagreement visible.
//
// IF YOU CHANGE THIS, change it here. Both call sites read `sectorSourceFor`, and
// tests/investment-reporting/sectorSourcePolicy.test.ts pins the behaviour.

/** Which source actually produced a fund's sector split on this render. */
export type SectorSourceKind =
  /** Manual mapping used at 100%. FE either absent, unresolvable, or deliberately not used. */
  | "manual"
  /** No manual mapping exists; FE's resolved breakdown used at 100%. */
  | "fe_fallback"
  /** Neither source is usable — the fund contributes nothing and must be excluded, not zeroed. */
  | "none";

export interface SectorSource {
  kind: SectorSourceKind;
  /** Multiplier applied to the manual mapping's contribution. */
  manualShare: number;
  /** Multiplier applied to FE's resolved allocation. */
  feShare: number;
  /**
   * Short, display-ready reason. Surfaced per fund so a table that mixes sources is legible as
   * mixed — the previous silent fallback meant 5 of 27 funds were built a different way from the
   * other 22 with nothing on screen saying so.
   */
  note: string;
}

/**
 * Decide the source for ONE fund.
 *
 * @param hasManual  the fund has at least one ba_cbam_fund_sector_mappings row
 * @param hasFe      FE's breakdown for this fund RESOLVED — every column mapped, no
 *                   `[needs split]` / `Unknown` bucket, and the total within 1 of 100. A present
 *                   but unresolved FE row is NOT `hasFe`; that distinction is the caller's.
 */
export function sectorSourceFor(hasManual: boolean, hasFe: boolean): SectorSource {
  if (hasManual) {
    return {
      kind: "manual",
      manualShare: 1,
      feShare: 0,
      note: hasFe
        ? "manual mapping (FE available and agrees to be cross-checked, not blended)"
        : "manual mapping",
    };
  }
  if (hasFe) {
    return {
      kind: "fe_fallback",
      manualShare: 0,
      feShare: 1,
      note: "FE breakdown — no manual mapping exists for this fund",
    };
  }
  return {
    kind: "none",
    manualShare: 0,
    feShare: 0,
    note: "no usable source — fund excluded from the matrix",
  };
}
